import React, { createContext, useContext, useEffect, useState } from 'react';
import { App } from 'antd';
import { getAuth, signOut, deleteUser, getRedirectResult, GoogleAuthProvider } from 'firebase/auth';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { useTranslation } from 'next-i18next';
import { userUpdate } from '@/lib/service';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

type UserProviderType = {
  /**
   * total cart quantity
   */
  // total?: number;
  openLogin?: boolean;
  isLoginView?: boolean;
  openLoginResult?: boolean;
  /**
   * add cart callback function
   * @param params Product information added
   */
  onAddShop?: <P = any>(params: P) => Promise<{ data: P }>;
  onDeleteShopCarItem?: <P = any>(params: P) => Promise<{ data: P }>;
  onOpenLoginModal?: (open: boolean) => void;
  onChangedLoginView?: (isLoginView: boolean) => void;
  onChangedLoginResult?: (isLoginView: boolean) => void;
  onAddToken?: (token?: string, signInMethod?: string, idToken?: string) => void;
  onSignOut?: (showAlert?: boolean) => void;
  onDeleteUser?: () => void;

  /**
   * Refresh cart data
   */
  onRefreshCartData?: () => Promise<void>;

  isLogin?: boolean;

  onChangedIsFirstLogin?: (isFirstLogin: boolean) => void;
  isFirstLogin?: boolean;

  // share count
  onShareUnreadCount?: (shareCount?: number) => void;
  shareUnreadCount?: number;

  // credits count
  onCreditsChanged?: (count?: number) => void;
  creditsCount?: number;

  // gift view
  onShowGiftView?: (isShow?: boolean) => void;
  isShowGiftView?: boolean;

  isFirstCreation?: boolean;
  onChangedIsFirstCreation?: (isFirstCreation: boolean) => void;

  isFirstSuccessCreation?: boolean;
  onChangedIsFirstSuccessCreation?: (isFirstSuccessCreation: boolean) => void;

  isFirstShare?: boolean;
  onChangedIsFirstShare?: (isFirstShare: boolean) => void;
};

const Context = createContext<UserProviderType>({});

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const router = useRouter();
  const [openLogin, setOpenLogin] = useState<boolean>(false);
  const [isLoginView, setIsLoginView] = useState<boolean>(true);
  const [openLoginResult, setOpenLoginResult] = useState<boolean>(false);
  const [accountList, setAccountList] = useState([]);
  const [shareUnreadCount, setShareUnreadCount] = useState<number>(0);
  const [creditsCount, setCreditsCount] = useState<number>(0);
  // 是否显示顶部注册礼物提示栏，默认设置为 false，避免设置 true，否则如果是登录提示栏会一闪而过
  const [isShowGiftView, setIsShowGiftView] = useState<boolean>(false);
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);
  const [isFirstCreation, setIsFirstCreation] = useState<boolean>(false);
  const [isFirstSuccessCreation, setIsFirstSuccessCreation] = useState<boolean>(false);
  const [isFirstShare, setIsFirstShare] = useState<boolean>(false);
  const token = Cookies.get('userToken');
  const [isLogin, setIsLogin] = useState<boolean>(token != null && token != undefined && token.length > 0);

  const handleAddShopCarItem = async (params: any) => {
    return Promise.resolve(params);
  };

  useEffect(() => {
    // The initialization is to get the total number of shopping carts
    const token = Cookies.get('userToken');
    setIsLogin(token != null && token != undefined && token.length > 0);
  }, []);

  useEffect(() => {
    if (typeof window !== undefined) {
      setTimeout(() => {
        loadUserData();
      }, 100);
    }
  }, []);

  useEffect(() => {
    // 根据登录状态，决定是否要显示注册礼物提示栏
    if (isLogin) {
      setIsShowGiftView(false);
    } else {
      setIsShowGiftView(true);
    }
  }, [isLogin]);

  const getToken = (id: string, signInMethod: string) => {
    const pathUrl = typeof window !== undefined ? window.location.href : process.env.NEXT_APP_WEB_HOST;
    userUpdate(id, router.locale).then((res) => {
      if (res?.data?.first_login) {
        if (res.code == 0) {
          ga_logEvent(EventName.user_register_Success);
        } else {
          ga_logEvent(EventName.user_register_Fail);
        }
      }

      if (res?.code == 0) {
        router.replace(`/login-success?token=${res?.data?.token}&url=${pathUrl}`);
        ga_logEvent(EventName.user_Login_Success);
      } else {
        message.error(res.message);
      }
    });
  };

  const loadUserData = () => {
    const auth = getAuth();
    getRedirectResult(auth).then(async (result) => {
      if (result) {
        const credential = GoogleAuthProvider.credentialFromResult(result);

        const token = credential.accessToken;
        Cookies.set('accessToken', token, { expires: 7 });
        // ls_saveItem('accessToken', token)

        result.user.getIdToken().then((id) => {
          getToken(id, 'google');
          ga_logEvent(EventName.login, { method: 'Google' });
        });
      }
    });
  };

  const onOpenLoginModal = (open: boolean) => {
    setOpenLogin(open);
  };

  const onChangedLoginView = (open: boolean) => {
    setIsLoginView(open);
  };

  const onChangedLoginResult = (open: boolean) => {
    setOpenLoginResult(open);
  };

  const onChangedIsFirstLogin = (isFirstLogin: boolean) => {
    setIsFirstLogin(isFirstLogin);
  };

  const handleRefreshCartData = async () => {
    return new Promise<void>((resolve) => {
      message.success('Refresh successfully', 1.2, () => resolve(null));
    });
  };

  const onAddToken = (token?: string, signInMethod?: string, idToken?: string) => {
    if (token != undefined) {
      Cookies.set('userToken', token, { expires: 7 });
      Cookies.set('SignInMethod', signInMethod, { expires: 7 });
      Cookies.set('IdToken', idToken, { expires: 7 });
    } else {
      Cookies.remove('userToken');
      Cookies.remove('SignInMethod');
    }
    setIsLogin(token != undefined);
  };

  const onSignOut = (showAlert?: boolean) => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        if (showAlert) {
          message.success(t('SignOutSuccessful'));
          onAddToken(undefined);
          setShareUnreadCount(0);
        }
        onAddToken(undefined);
      })
      .catch((error) => {
        message.error(t('Log_Out_Failed_Error_Msg'));
      });
  };

  const onDeleteUser = () => {
    const auth = getAuth();
    const user = auth.currentUser;

    deleteUser(user)
      .then(() => {
        message.success(t('UserDeleted'));
        onAddToken(undefined);
        setAccountList([]);
        setShareUnreadCount(0);

        router.replace('/');
      })
      .catch((error) => {
        message.error(t('Account_Deletion_Failed_Error_Msg'));
      });
  };

  const onShareUnreadCount = (shareCount?: number) => {
    setShareUnreadCount(shareCount);
  };

  const onCreditsChanged = (count?: number) => {
    const credits = Number(count);
    setCreditsCount(isNaN(credits) ? 0 : Math.max(0, credits));
  };

  const onShowGiftView = (isShowGiftView: boolean) => {
    setIsShowGiftView(isShowGiftView);
  };

  const onChangedIsFirstCreation = (isFirstCreation: boolean) => {
    setIsFirstCreation(isFirstCreation);
  };

  const onChangedIsFirstSuccessCreation = (isFirstSuccessCreation: boolean) => {
    setIsFirstSuccessCreation(isFirstSuccessCreation);
  };

  const onChangedIsFirstShare = (isFirstShare: boolean) => {
    setIsFirstShare(isFirstShare);
  };

  const exposed = {
    openLogin,
    isLoginView,
    openLoginResult,
    // total: totalCartQuantity,
    isLogin,
    isFirstLogin,
    onChangedIsFirstLogin,
    accountList,
    onAddShop: handleAddShopCarItem,
    onOpenLoginModal,
    onChangedLoginView,
    onChangedLoginResult,
    onRefreshCartData: handleRefreshCartData,
    onAddToken,
    onSignOut,
    onDeleteUser,
    onShareUnreadCount,
    shareUnreadCount,
    onCreditsChanged,
    creditsCount,
    onShowGiftView,
    isShowGiftView,
    isFirstCreation,
    onChangedIsFirstCreation,
    isFirstSuccessCreation,
    onChangedIsFirstSuccessCreation,
    isFirstShare,
    onChangedIsFirstShare
  };

  return <Context.Provider value={exposed}>{children}</Context.Provider>;
};

export const useUserProvider = () => useContext(Context);

export default UserProvider;
